import type { ChartLine } from "components/charting/types/Chart.types";
import type { OneRepMaxEstimate } from "components/charting/AthleteProfilingChart/useAthleteProfilingChartAnnotations";
import type { ProfilingChartEntityId } from "components/charting/types";
import type { MeasurementSet, Variants } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";
import ArrayHelpers from "helpers/ArrayHelpers";

class LoadVelocityProfileHelpers {
  static isLoadVelocityProfile(option: LoadVelocityProfile | MeasurementSet): option is LoadVelocityProfile {
    return "slope" in option;
  }

  static getKey(lvp: Pick<LoadVelocityProfile, "exerciseId" | "variant" | "createdAt">) {
    return ["lvp", lvp.exerciseId, lvp.variant, lvp.createdAt].filter(Boolean).join("-");
  }

  /** Swaps each selected profile for the offered one with the same key, dropping any no longer offered. */
  static reconcile(selected: LoadVelocityProfile[], offered: LoadVelocityProfile[]) {
    return ArrayHelpers.reconcile(selected, offered, LoadVelocityProfileHelpers.getKey);
  }

  /** The profile a chart line was drawn from, if it is a load velocity profile line. */
  static getLineLvp(line: Pick<ChartLine, "line">): LoadVelocityProfile | undefined {
    const point = line.line[0];
    return point?.metadataType === "Exercise" ? point.lvp : undefined;
  }

  static partition(options: (LoadVelocityProfile | MeasurementSet)[]) {
    const profiles: LoadVelocityProfile[] = [];
    const sets: MeasurementSet[] = [];

    options.forEach((option) => {
      if (LoadVelocityProfileHelpers.isLoadVelocityProfile(option)) {
        profiles.push(option);
      } else {
        sets.push(option);
      }
    });

    return { profiles, sets };
  }

  /**
   * Partitions a selection where a profile and sets exclude each other, keeping the kind just picked.
   * Only one profile is kept, so picking another swaps it in.
   */
  static partitionSelection(
    selected: (LoadVelocityProfile | MeasurementSet)[],
    picked?: LoadVelocityProfile | MeasurementSet
  ) {
    const { profiles, sets } = LoadVelocityProfileHelpers.partition(selected);
    if (picked && !LoadVelocityProfileHelpers.isLoadVelocityProfile(picked)) return { profiles: [], sets };
    const keptProfiles = picked && profiles.includes(picked) ? [picked] : profiles;
    return { profiles: keptProfiles, sets: keptProfiles.length ? [] : sets };
  }

  /** A load as a rounded percentage of its side's estimated 1RM; an estimate without a variant covers both sides. */
  static getOneRepMaxPercentage(
    estimates: Pick<OneRepMaxEstimate, "entityId" | "variant" | "estimatedOneRepMax">[],
    point: { load: number; side: ProfilingChartEntityId; variant?: Variants }
  ) {
    const estimate = estimates.find(
      ({ entityId, variant }) => entityId === point.side && (!variant || variant === point.variant)
    );
    return estimate ? Math.round((point.load / estimate.estimatedOneRepMax) * 100) : undefined;
  }
}

export default LoadVelocityProfileHelpers;
